import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
    Toaster,
    useToastController,
    Toast,
    ToastTitle,
    ToastBody,
    ToastTrigger,
    Button,
    useId
} from '@fluentui/react-components';
import {
    CheckmarkCircle24Regular,
    Warning24Regular,
    ErrorCircle24Regular,
    Info24Regular,
    Dismiss24Regular
} from '@fluentui/react-icons';
import { RootState, removeEvaluationFeedback } from '../../utils/store';

const EvaluationFeedbackBar: React.FC = () => {
    const dispatch = useDispatch();
    const evaluationFeedback = useSelector((state: RootState) => state.settings.evaluationFeedback);
    const currentSessionId = useSelector((state: RootState) => state.settings.sessionId);
    
    const toasterId = useId('evaluation-toaster');
    const { dispatchToast } = useToastController(toasterId);

    const getToastIntent = (evaluationType: 'unsure' | 'mismatch' | 'info') => {
        switch (evaluationType) {
            case 'mismatch':
                return 'error';
            case 'unsure':
                return 'warning';
            case 'info':
            default:
                return 'info';
        }
    };

    const getToastIcon = (evaluationType: 'unsure' | 'mismatch' | 'info') => {
        switch (evaluationType) {
            case 'mismatch':
                return <ErrorCircle24Regular />;
            case 'unsure':
                return <Warning24Regular />;
            case 'info':
            default:
                return <Info24Regular />;
        }
    };

    const getToastTitle = (evaluationType: 'unsure' | 'mismatch' | 'info') => {
        switch (evaluationType) {
            case 'mismatch':
                return 'Mismatch';
            case 'unsure':
                return 'Warning';
            case 'info':
            default:
                return 'Info';
        }
    };

    // Watch for new evaluation feedback and dispatch toasts
    useEffect(() => {
        if (evaluationFeedback.length > 0) {
            // Filter feedback to only show items for the current session
            const currentSessionFeedback = evaluationFeedback.filter(
                feedback => feedback.sessionId === currentSessionId
            );
            
            if (currentSessionFeedback.length > 0) {
                // Get the most recent feedback item for the current session
                const latestFeedback = currentSessionFeedback[currentSessionFeedback.length - 1];
                
                // Check if this feedback was just added (within the last 100ms)
                const isNewFeedback = Date.now() - latestFeedback.timestamp < 100;
                
                if (isNewFeedback) {
                    const intent = getToastIntent(latestFeedback.evaluationType);
                    const icon = getToastIcon(latestFeedback.evaluationType);
                    const title = getToastTitle(latestFeedback.evaluationType);
                    
                    dispatchToast(
                        <Toast>
                            <ToastTitle media={icon} action={
                                <ToastTrigger>
                                    <Button
                                        appearance="subtle"
                                        icon={<Dismiss24Regular />}
                                        size="small"
                                        aria-label="Dismiss toast"
                                    />
                                </ToastTrigger>
                            }>
                                {title}
                            </ToastTitle>
                            <ToastBody>{latestFeedback.message}</ToastBody>
                        </Toast>,
                        {
                            intent: intent as any,
                            timeout: 30000,
                            onStatusChange: (ev, data) => {
                                // Remove from store when toast is dismissed
                                if (data.status === 'dismissed') {
                                    dispatch(removeEvaluationFeedback(latestFeedback.timestamp));
                                }
                            }
                        }
                    );
                }
            }
        }
    }, [evaluationFeedback, currentSessionId, dispatchToast, dispatch]);

    return <Toaster toasterId={toasterId} position="top-end" />;
};

export default EvaluationFeedbackBar;